import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/global.css';
import '../styles/pages/Landing.css';
import CustomToolbar from './components/CustomToolbar';
import * as mapas from '../utils/mapas'
import { Dock } from 'primereact/dock';
import { MenuItem } from 'primereact/menuitem';
import { ReactComponent as BackIcon } from '../images/back.svg';
import { ReactComponent as ListIcon } from '../images/book-library-svgrepo-com.svg';
import Auth from '../auth/Authenticator';
import L from 'leaflet';
import proj4 from 'proj4';
import * as turf from '@turf/turf';
import DialogEspecies from './dialogs/DialogEspecies';
import { ProgressSpinner } from 'primereact/progressspinner';

type EspecieType = {
    id: number;
    familia: string;
    genero: string;
    nome_cientifico: string;
    nome_popular: string;
    forma_vida: string;
    origem: string;
    dispersao?: string;
    fenologia?: string;
    polinizacao?: string;
    status_pr?: string;
    plantada?: boolean;
};

function Unidades() {
    const auth = Auth.useAuth();
    const mapRef = useRef<any>(null);
    const [mapa, setMapa] = useState<L.Map | null>(null)
    const navigator = useNavigate()
    const [setores, setSetores] = useState<any[]>([]);
    const [unidades, setUnidades] = useState<any[]>([]);
    const [subunidades, setSubunidades] = useState<any[]>([]);
    const [arvores, setArvores] = useState<any[]>([]);
    const [unidadesLayerGroup] = useState(L.layerGroup());
    const [subunidadesLayerGroup] = useState(L.layerGroup());
    const [setoresLayerGroup] = useState(L.layerGroup());
    const [arvoreslayerGroup] = useState(L.layerGroup());
    const [showEspeciesDialog, setShowEspeciesDialog] = useState(false);
    const [especies, setEspecies] = useState<EspecieType[]>([]);
    const [loadingEspecies, setLoadingEspecies] = useState(false);
    const [loadingMapa, setLoadingMapa] = useState(false);
    const [loadingUnidades, setLoadingUnidades] = useState(false);
    const [loadingSubunidades, setLoadingSubunidades] = useState(false);
    const abortControllerSubunidades = useRef<AbortController | null>(null);
    const canvasRendererRef = useRef<L.Canvas>(L.canvas({ padding: 0.5 }));

    useEffect(() => {
        // Carregar espécies quando o componente monta
        const carregarEspecies = async () => {
            setLoadingEspecies(true);
            try {
                const response = await fetch('https://api.itaipu.damine.com.br/api/especies/');
                const data = await response.json();
                setEspecies(data.results || data);
            } catch (error) {
                console.error("Erro ao carregar espécies:", error);
            } finally {
                setLoadingEspecies(false);
            }
        };

        carregarEspecies();
    }, []);
    
    useEffect(() => {
        if (!mapa) {
            // Coordenadas da Usina Itaipu
            const map = L.map(mapRef.current).setView([-25.4044, -54.5797], 14);
            mapas.mapas.googleHybrid().addTo(map);
            mapas.scale.addTo(map);

            L.control.groupedLayers({
                'Base': {
                    'Google': mapas.mapas.googleHybrid(),
                    'ESRI': mapas.mapas.esri(),
                    'ArcGis': mapas.mapas.arcgis(),
                }
            }, {
                'Dados': {
                    'Setores Inventariáveis': setoresLayerGroup.addTo(map),
                    'Unidades Amostrais': unidadesLayerGroup.addTo(map),
                    'Subunidades Amostrais': subunidadesLayerGroup.addTo(map),
                    'Árvores': arvoreslayerGroup.addTo(map),
                }
            }).addTo(map);

            setMapa(map);
            map.invalidateSize();
        }
    }, [mapa, unidadesLayerGroup, subunidadesLayerGroup, setoresLayerGroup, arvoreslayerGroup]);

    const isLatLng = (coords: number[]) =>
        coords[0] >= -180 && coords[0] <= 180 &&
        coords[1] >= -90 && coords[1] <= 90;

    const hasLatLngGeometry = (geom: any) => {
        if (!geom || (geom.type !== "Polygon" && geom.type !== "MultiPolygon")) return false;

        const coord = geom.type === "Polygon" ? geom.coordinates[0][0] : geom.coordinates[0][0][0];
        return isLatLng(coord);
    };

    const createPopupContent = (props: any) => {
        let popupContent = '<div style="max-height: 250px; overflow-y: auto; font-size: 12px;">';
        for (const key in props) {
            if (Object.prototype.hasOwnProperty.call(props, key)) {
                const value = props[key];
                let displayValue;
                const displayKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                if (value === null || value === undefined) {
                    displayValue = 'N/A';
                } else if (typeof value === 'object') {
                    if (key === 'centroide' && value.type === 'Point' && value.coordinates) {
                        displayValue = `Lat: ${value.coordinates[1].toFixed(6)}, Lon: ${value.coordinates[0].toFixed(6)}`;
                    } else if (key === 'subunidades' && value.type === 'FeatureCollection') {
                        displayValue = `Contém ${value.features.length} subunidades`;
                    } else {
                        displayValue = JSON.stringify(value);
                    }
                } else if (typeof value === 'boolean') {
                    displayValue = value ? 'Sim' : 'Não';
                } else if (key === 'url' && typeof value === 'string' && value.startsWith('http')) {
                    popupContent += `<b>${displayKey}:</b> <a href="${value}" target="_blank" rel="noopener noreferrer">Ver detalhes</a><br/>`;
                    continue;
                } else {
                    displayValue = value;
                }
                popupContent += `<b>${displayKey}:</b> ${displayValue}<br/>`;
            }
        }
        return popupContent + '</div>';
    };

    const carregarSetores = async (map: L.Map) => {
        try {
            const response = await fetch('https://api.itaipu.damine.com.br/api/setores/');
            const data = await response.json();

            const utmProjection = '+proj=utm +zone=21 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs';
            const wgs84Projection = 'EPSG:4326';

            const validFeatures = data.features
                .filter((f: any) => f.geometry)
                .map((feature: any) => {
                    const reprojectedFeature = JSON.parse(JSON.stringify(feature));
                    
                    // Otimizado: transformar coordenadas de forma mais eficiente
                    if (reprojectedFeature.geometry.type === 'Polygon') {
                        reprojectedFeature.geometry.coordinates = reprojectedFeature.geometry.coordinates.map((ring: any) =>
                            ring.map((coord: any) => {
                                const [lon, lat] = proj4(utmProjection, wgs84Projection, coord);
                                return [lon, lat];
                            })
                        );
                    } else if (reprojectedFeature.geometry.type === 'MultiPolygon') {
                        reprojectedFeature.geometry.coordinates = reprojectedFeature.geometry.coordinates.map((polygon: any) =>
                            polygon.map((ring: any) =>
                                ring.map((coord: any) => {
                                    const [lon, lat] = proj4(utmProjection, wgs84Projection, coord);
                                    return [lon, lat];
                                })
                            )
                        );
                    }
                    
                    return reprojectedFeature;
                });

            if (validFeatures.length === 0) {
                setSetores([]);
                console.warn("Nenhuma geometria de Setores válida encontrada.");
                return;
            }

            const geoJsonLayer = L.geoJSON(validFeatures, {
                style: {
                    renderer: canvasRendererRef.current,
                    color: '#008000',
                    weight: 2,
                    opacity: 0.7,
                    fillColor: '#008000',
                    fillOpacity: 0.2
                },
                onEachFeature: (feature, layer) => {
                    layer.bindPopup(createPopupContent(feature.properties));
                }
            });

            setoresLayerGroup.clearLayers().addLayer(geoJsonLayer);
            setSetores(validFeatures);
            
            // Fazer flyToBounds dos setores
            const bounds = geoJsonLayer.getBounds();
            if (bounds.isValid()) {
                map.flyToBounds(bounds, { padding: [50, 50], duration: 1.5 });
            }

        } catch (e) {
            console.error("Erro ao carregar Setores:", e);
        }
    };

    const carregarUnidades = async () => {
        if (!mapa) return;

        setLoadingUnidades(true);
        try {
            const response = await fetch(`https://api.itaipu.damine.com.br/api/unidades/`);
            const data = await response.json();
            console.log('Unidades Data:', data);

            const validFeatures = data.features.filter((f: any) => f.geometry && hasLatLngGeometry(f.geometry));
            setUnidades(validFeatures);

            const geoJsonLayer = L.geoJSON(validFeatures, {
                style: {
                    renderer: canvasRendererRef.current,
                    color: '#ff7800',
                    weight: 5,
                    opacity: 0.65,
                    fillOpacity: 0.2
                },
                onEachFeature: (feature, layer) => {
                    layer.bindPopup(createPopupContent(feature.properties));
                }
            });

            unidadesLayerGroup.clearLayers().addLayer(geoJsonLayer);

        } catch (e: any) {
            console.error("Erro ao carregar Unidades:", e);
        } finally {
            setLoadingUnidades(false);
        }
    };

    const carregarSubunidades = async () => {
        if (!mapa) return;
        const zoom = mapa.getZoom();

        // Otimização: Zoom 17+ para Subunidades e Árvores
        if (zoom < 17) {
            subunidadesLayerGroup.clearLayers();
            arvoreslayerGroup.clearLayers();
            return;
        }

        if (abortControllerSubunidades.current) {
            abortControllerSubunidades.current.abort();
        }
        const controller = new AbortController();
        abortControllerSubunidades.current = controller;

        setLoadingSubunidades(true);
        try {
            const bounds = mapa.getBounds();
            const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
            
            const url = `https://api.itaipu.damine.com.br/api/subunidades/?in_bbox=${bbox}`;
            
            const response = await fetch(url, {
                signal: controller.signal
            });
            const data = await response.json();

            // Flexibilidade para diferentes formatos de resposta (GeoJSON features ou DRF results)
            const features = data.features || data.results || (Array.isArray(data) ? data : []);
            
            const utmProjection = '+proj=utm +zone=21 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs';
            const wgs84Projection = 'EPSG:4326';

            const subunidadesFeatures: any[] = [];
            const arvoresFeatures: any[] = [];

            features.forEach((feature: any) => {
                // Processar Subunidade
                if (feature.geometry) {
                    const subunidadeReprojected = JSON.parse(JSON.stringify(feature));
                    const firstCoord = subunidadeReprojected.geometry.type === "Polygon"
                        ? subunidadeReprojected.geometry.coordinates[0][0]
                        : subunidadeReprojected.geometry.coordinates[0][0][0];

                    if (Math.abs(firstCoord[0]) > 180 || Math.abs(firstCoord[1]) > 90) {
                        if (subunidadeReprojected.geometry.type === 'Polygon') {
                            subunidadeReprojected.geometry.coordinates = subunidadeReprojected.geometry.coordinates.map((ring: any) =>
                                ring.map((coord: any) => {
                                    const [lon, lat] = proj4(utmProjection, wgs84Projection, coord);
                                    return [lon, lat];
                                })
                            );
                        } else if (subunidadeReprojected.geometry.type === 'MultiPolygon') {
                            subunidadeReprojected.geometry.coordinates = subunidadeReprojected.geometry.coordinates.map((polygon: any) =>
                                polygon.map((ring: any) =>
                                    ring.map((coord: any) => {
                                        const [lon, lat] = proj4(utmProjection, wgs84Projection, coord);
                                        return [lon, lat];
                                    })
                                )
                            );
                        }
                    }
                    subunidadesFeatures.push(subunidadeReprojected);
                }

                // Processar Árvores dentro da Subunidade
                if (feature.properties?.arvores?.features) {
                    feature.properties.arvores.features.forEach((arvore: any) => {
                        // Assumindo que árvores podem precisar de reprojeção se estiverem em UTM
                        if (arvore.geometry && arvore.geometry.type === 'Point') {
                            const [x, y] = arvore.geometry.coordinates;
                            if (Math.abs(x) > 180 || Math.abs(y) > 90) {
                                const [lon, lat] = proj4(utmProjection, wgs84Projection, [x, y]);
                                arvore.geometry.coordinates = [lon, lat];
                            }
                            arvoresFeatures.push(arvore);
                        }
                    });
                }
            });

            setSubunidades(subunidadesFeatures);
            setArvores(arvoresFeatures);

            // Renderizar Subunidades
            if (subunidadesFeatures.length > 0) {
                const subunidadesGeoJsonLayer = L.geoJSON(subunidadesFeatures, {
                    style: {
                        renderer: canvasRendererRef.current,
                        color: '#00FFFF',
                        weight: 2,
                        opacity: 0.9,
                        fillOpacity: 0.4
                    },
                    onEachFeature: (feature, layer) => {
                        layer.bindPopup(createPopupContent(feature.properties));
                    }
                });
                subunidadesLayerGroup.clearLayers().addLayer(subunidadesGeoJsonLayer);
            } else {
                subunidadesLayerGroup.clearLayers();
            }

            const canvasRenderer = canvasRendererRef.current;

            // Criar marcadores para cada árvore
            const markers = arvoresFeatures.map((feature: any) => {
                if (!feature.geometry || feature.geometry.type !== 'Point') {
                    return null;
                }

                const [lon, lat] = feature.geometry.coordinates;
                const props = feature.properties || {};

                // Criar um marcador com ícone customizado
                const marker = L.circleMarker([lat, lon], {
                    renderer: canvasRenderer, 
                    radius: 6,
                    fillColor: '#2ecc71',
                    color: '#27ae60',
                    weight: 2,
                    opacity: 0.8,
                    fillOpacity: 0.7
                });

                // Criar conteúdo do popup
                let popupContent = '<div style="max-height: 250px; overflow-y: auto; font-size: 12px;">';
                popupContent += `<b>ID:</b> ${feature.id}<br/>`;
                popupContent += `<b>Subunidade:</b> ${props.subunidade || 'N/A'}<br/>`;
                popupContent += `<b>Plaqueta:</b> ${props.plaqueta || 'N/A'}<br/>`;
                popupContent += `<b>Espécie:</b> ${props.especie?.nome_popular || 'N/A'}<br/>`;
                popupContent += `<b>Forma de Vida:</b> ${props.especie?.forma_vida || 'N/A'}<br/>`;
                popupContent += `<b>Origem:</b> ${props.especie?.origem || 'N/A'}<br/>`;
                popupContent += `<b>Fustes:</b> ${props.fustes?.length || 0}<br/>`;
                popupContent += `<b>Latitude:</b> ${lat.toFixed(6)}<br/>`;
                popupContent += `<b>Longitude:</b> ${lon.toFixed(6)}<br/>`;
                if (props.coord_x || props.coord_y) {
                    popupContent += `<b>Coordenadas Locais:</b> X: ${props.coord_x}, Y: ${props.coord_y}<br/>`;
                }
                if (props.url) {
                    popupContent += `<a href="${props.url}" target="_blank" rel="noopener noreferrer">Ver detalhes</a>`;
                }
                popupContent += '</div>';

                marker.bindPopup(popupContent);
                return marker;
            }).filter((m): m is L.CircleMarker => m !== null);

            // Criar layer group com todos os marcadores
            const arvoresLayer = L.featureGroup(markers);
            arvoreslayerGroup.clearLayers().addLayer(arvoresLayer);

        } catch (e: any) {
            if (e.name === 'AbortError') return;
            console.error("Erro ao carregar Subunidades e Árvores:", e);
        } finally {
            if (abortControllerSubunidades.current === controller) {
                setLoadingSubunidades(false);
            }
        }
    };

    const zoomToFeature = (feature: any) => {
        if (!mapa || !feature.geometry) return;
        
        setShowEspeciesDialog(false); // Fecha o diálogo

        if (feature.geometry.type === 'Point') {
            const [lon, lat] = feature.geometry.coordinates;
            mapa.flyTo([lat, lon], 19, { duration: 1.5 });
        } else {
            const layer = L.geoJSON(feature);
            const bounds = layer.getBounds();
            if (bounds.isValid()) {
                mapa.flyToBounds(bounds, { padding: [50, 50], duration: 1.5 });
            }
        }
    };

    useEffect(() => {
        if (!mapa) return;

        // Carregar dados estáticos
        const carregarDadosIniciais = async () => {
            setLoadingMapa(true);
            try {
                // Carrega setores primeiro
                await carregarSetores(mapa);
                // Carrega unidades apenas após o término dos setores, garantindo a ordem de empilhamento
                await carregarUnidades();
            } catch (error) {
                console.error("Erro ao carregar dados do mapa:", error);
            } finally {
                setLoadingMapa(false);
            }
        };

        carregarDadosIniciais();

        const onMoveEnd = () => {
            carregarSubunidades();
        };

        const onZoomEnd = () => {
            // moveend já cobre a maioria dos casos, mas zoomend garante a atualização do log
        };

        mapa.on('moveend', onMoveEnd);
        mapa.on('zoomend', onZoomEnd);
        carregarSubunidades(); // Carga inicial

        return () => {
            mapa.off('moveend', onMoveEnd);
            mapa.off('zoomend', onZoomEnd);
        };
    }, [mapa])

    const EspeciesMenu = {
        label: 'Espécies', 
        icon: () => loadingEspecies ? <ProgressSpinner style={{width: '2rem', height: '2rem'}} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" /> : <ListIcon />,
        command: () => {
            setShowEspeciesDialog(true);
        }
    }
    const VoltarMenu = {
        label: 'Sair', icon: () => <BackIcon />,
        command: () => {
            navigator('/')
        }
    }
    const items: MenuItem[] = [EspeciesMenu,VoltarMenu];

    return (
        <div className='landing'>
            <CustomToolbar titulo={'INVENTÁRIO FLORESTAL ITAIPU - DAMINE'}
                loading={loadingMapa || loadingEspecies || loadingUnidades || loadingSubunidades} user={auth.user}
            />

            <div id='map' ref={mapRef} />
            <div className="dock-window" style={{ zIndex: 1 }}>
                <Dock model={items} position={'bottom'} />
            </div>
            <DialogEspecies 
                visible={showEspeciesDialog} 
                setVisible={setShowEspeciesDialog} 
                especies={especies} 
                setores={setores}
                unidades={unidades}
                subunidades={subunidades}
                arvores={arvores}
                loading={loadingEspecies || loadingUnidades || loadingSubunidades} 
                onSelect={zoomToFeature}
            />
        </div>
    );
}

export default Unidades;